import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { confirmPasswordReset } from '@services/api';
import type { ApiError } from '@services/api';
import { SaaeLogo } from '@components/SaaeLogo';
import { Button } from '@components/Button/Button';
import { InputField } from '@components/Input';
import { useLanguage } from '@hooks/useLanguage';
import { resetPasswordSchema, type ResetPasswordValues } from '@schemas/formSchemas';

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isRtl = language === 'ar';

  const form = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { newPassword: '', confirm: '' },
  });

  async function handleSubmit(values: ResetPasswordValues) {
    if (!token) {
      setError(t('admin.passwordReset.expired'));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await confirmPasswordReset(token, values.newPassword);
      navigate('/login', { replace: true });
    } catch (err) {
      const ax = err as ApiError;
      setError(ax.message ?? t('admin.passwordReset.expired'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center bg-surface-web px-4 py-10 font-sans"
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <div className="mb-8 flex justify-center">
        <SaaeLogo size="small" mode="light" className="!h-9" />
      </div>
      <div className="w-full max-w-md rounded-card border border-surface-border bg-surface-cards p-6 shadow-sm sm:p-8">
        <h1 className="mb-1 text-center text-xl font-bold text-primary font-sans sm:text-2xl">
          {t('admin.passwordReset.setNewTitle')}
        </h1>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="mt-6 flex flex-col gap-4" noValidate>
          <InputField
            label={t('admin.passwordReset.newPasswordLabel')}
            type="password"
            autoComplete="new-password"
            size="lg"
            fullWidth
            state={form.formState.errors.newPassword ? 'error' : 'default'}
            {...form.register('newPassword')}
          />
          {form.formState.errors.newPassword && (
            <p className="text-xs text-surface-error-primary">{form.formState.errors.newPassword.message}</p>
          )}
          <InputField
            label={t('admin.passwordReset.confirmLabel')}
            type="password"
            autoComplete="new-password"
            size="lg"
            fullWidth
            state={form.formState.errors.confirm ? 'error' : 'default'}
            {...form.register('confirm')}
          />
          {form.formState.errors.confirm && (
            <p className="text-xs text-surface-error-primary">{form.formState.errors.confirm.message}</p>
          )}
          {error && (
            <p className="rounded-[16px] border border-surface-error-primary bg-surface-error-lighter/50 px-4 py-3 text-sm text-surface-error-primary font-sans" role="alert">
              {error}
            </p>
          )}
          <Button type="submit" variant="primary" size="lg" fullWidth disabled={loading || !token} loading={loading}>
            {t('admin.passwordReset.setNewSubmit')}
          </Button>
        </form>
      </div>
    </div>
  );
}
